import { toast } from 'sonner';
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { resolveUserRoleState } from '@/lib/authRoles';
import type { AppRole } from '@/lib/authRoles';
import { detachNativePushForSignOut, refreshPushRegistration } from '@/utils/pushNotifications';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: AppRole | null;
  userRoles: AppRole[];
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const applyRoleState = async (
  userId: string,
  setUserRoles: (roles: AppRole[]) => void,
  setUserRole: (role: AppRole | null) => void,
  setIsAdmin: (isAdmin: boolean) => void,
) => {
  const roleState = await resolveUserRoleState(userId);
  setUserRoles(roleState.userRoles);
  setUserRole(roleState.userRole);
  setIsAdmin(roleState.isAdmin);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) void refreshPushRegistration().catch(() => {
      console.error('[push] could not refresh registration');
    });
  }, [user?.id]);

  useEffect(() => {
    // Function to process any pending claim from localStorage
    const processPendingClaim = async () => {
      const pendingClaimRaw = localStorage.getItem('pendingClaim');
      if (!pendingClaimRaw) return;

      try {
        const pendingClaim = JSON.parse(pendingClaimRaw);
        if (pendingClaim?.supplierId) {
          console.log('Processing pending claim for supplier:', pendingClaim.supplierId);
          
          const { data, error } = await supabase.functions.invoke('claim-listing', {
            body: { supplierId: pendingClaim.supplierId },
          });

          if (error) {
            console.error('Pending claim error:', error);
          } else if ((data as any)?.error) {
            console.error('Pending claim error:', (data as any).error);
          } else {
            console.log('Pending claim successful');
            localStorage.removeItem('pendingClaim');
            
            // Redirect to appropriate dashboard using React Router navigation
            const dashboardPath = pendingClaim.supplierType === 'venue' 
              ? '/venue/dashboard' 
              : '/supplier/dashboard';
            
            // Only redirect if not already on a dashboard - use navigate instead of window.location
            if (!window.location.pathname.includes('/dashboard')) {
              navigate(dashboardPath);
            }
          }
        }
      } catch (e) {
        console.error('Error processing pending claim:', e);
        localStorage.removeItem('pendingClaim');
      }
    };

    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Fetch user role when session changes
        if (session?.user) {
          // Keep loading=true until roles resolve, otherwise role-gated pages
          // (e.g. /admin) see isAdmin=false and redirect away.
          setTimeout(async () => {
            await applyRoleState(session.user.id, setUserRoles, setUserRole, setIsAdmin);
            setLoading(false);
            
            // Process any pending claim after login
            if (event === 'SIGNED_IN') {
              await processPendingClaim();
            }
          }, 0);
        } else {
          setUserRole(null);
          setUserRoles([]);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        applyRoleState(session.user.id, setUserRoles, setUserRole, setIsAdmin)
          .then(() => {
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      if (user?.id) await detachNativePushForSignOut(user.id);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not sign out. Please try again.');
      throw error;
    }
    setUser(null);
    setSession(null);
    setUserRole(null);
    setUserRoles([]);
    setIsAdmin(false);
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ user, session, userRole, userRoles, isAdmin, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Private demo only (src/components/demo/DemoWorkspace.tsx): gives the real screens a fictional signed-in
 * account. It carries no real session token, and the real sign-in above is left untouched.
 */
export const DemoAuthProvider = ({ user, role, children }: { user: User; role: AppRole; children: ReactNode }) => (
  <AuthContext.Provider value={{
    user,
    session: { access_token: "demo", refresh_token: "demo", token_type: "bearer", expires_in: 3600, user } as Session,
    userRole: role,
    userRoles: [role],
    isAdmin: false,
    loading: false,
    signOut: async () => {},
  }}>
    {children}
  </AuthContext.Provider>
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Return a safe default during initial render before AuthProvider mounts
    // This can happen during HMR or initial load race conditions
    console.warn('useAuth called outside AuthProvider - returning safe defaults');
    return {
      user: null,
      session: null,
      userRole: null,
      userRoles: [],
      isAdmin: false,
      loading: true,
      signOut: async () => {},
    } as AuthContextType;
  }
  return context;
};
